import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, List, ListItem,
  ListItemText, ListItemSecondaryAction, IconButton, Chip,
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  Schedule as QueuedIcon
} from '@mui/icons-material';
import axios from 'axios';

const QueuePanel = () => {
  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchQueueStatus();
    // Auto-refresh every 5 seconds when processing
    const interval = setInterval(() => {
      if (processing) {
        fetchQueueStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [processing]);

  const fetchQueueStatus = async () => {
    try {
      const response = await axios.get('/api/queue');
      setQueueData(response.data);
      setProcessing(response.data.processing);
    } catch (error) {
      console.error('Error fetching queue status:', error);
      showSnackbar('Error al obtener estado de la cola', 'error');
    } finally {
      setLoading(false);
    }
  };

  const startProcessing = async () => {
    try {
      setProcessing(true);
      await axios.post('/api/queue/process');
      showSnackbar('Procesamiento de cola iniciado', 'success');
      fetchQueueStatus();
    } catch (error) {
      console.error('Error starting queue processing:', error);
      showSnackbar('Error al iniciar procesamiento', 'error');
      setProcessing(false);
    }
  };

  const removeFromQueue = async (itemId) => {
    try {
      await axios.delete(`/api/queue/${itemId}`);
      showSnackbar('Elemento removido de la cola', 'success');
      fetchQueueStatus();
    } catch (error) {
      console.error('Error removing from queue:', error);
      showSnackbar('Error al remover elemento', 'error');
    }
  };

  const clearCompleted = async () => {
    try {
      await axios.post('/api/queue/clear-completed');
      showSnackbar('Elementos completados limpiados', 'success');
      fetchQueueStatus();
    } catch (error) {
      console.error('Error clearing completed:', error);
      showSnackbar('Error al limpiar completados', 'error');
    }
  };

  const clearFailed = async () => {
    try {
      await axios.post('/api/queue/clear-failed');
      showSnackbar('Elementos fallidos limpiados', 'success');
      fetchQueueStatus();
    } catch (error) {
      console.error('Error clearing failed:', error);
      showSnackbar('Error al limpiar fallidos', 'error');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('¿Estás seguro de que quieres limpiar toda la cola?')) {
      return;
    }

    try {
      await axios.post('/api/queue/clear-all');
      showSnackbar('Cola limpiada completamente', 'success');
      fetchQueueStatus();
    } catch (error) {
      console.error('Error clearing all:', error);
      showSnackbar('Error al limpiar cola', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CompletedIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'processing': return <PlayIcon color="primary" />;
      case 'queued': return <QueuedIcon color="action" />;
      default: return <QueuedIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'primary';
      case 'queued': return 'default';
      default: return 'default';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Card sx={{ minWidth: 300, maxWidth: 400 }}>
        <CardContent>
          <Typography variant="h6">⏳ Cola de Procesamiento</Typography>
          <Typography>Cargando...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!queueData) {
    return (
      <Card sx={{ minWidth: 300, maxWidth: 400 }}>
        <CardContent>
          <Typography variant="h6">⏳ Cola de Procesamiento</Typography>
          <Typography color="error">Error al cargar datos</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ minWidth: 300, maxWidth: 400 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">⏳ Cola de Procesamiento</Typography>
            <IconButton size="small" onClick={fetchQueueStatus} title="Actualizar">
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Queue Statistics */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {queueData.queue_length} | Completados: {queueData.completed_count} | Fallidos: {queueData.failed_count}
            </Typography>
          </Box>

          {/* Current Job */}
          {queueData.current_job && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Procesando: {queueData.current_job}
              </Typography>
            </Box>
          )}

          {/* Control Buttons */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayIcon />}
              onClick={startProcessing}
              disabled={processing || queueData.queue_length === 0}
              fullWidth
            >
              {processing ? 'Procesando...' : 'Iniciar'}
            </Button>
          </Box>

          {/* Management Buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CompletedIcon />}
              onClick={clearCompleted}
              disabled={queueData.completed_count === 0}
            >
              Limpiar OK
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ErrorIcon />}
              onClick={clearFailed}
              disabled={queueData.failed_count === 0}
            >
              Limpiar ERR
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<ClearIcon />}
              onClick={clearAll}
              disabled={queueData.queue_length === 0}
            >
              Limpiar Todo
            </Button>
          </Box>

          {/* Queue Items */}
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {queueData.queue.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Cola vacía
              </Typography>
            ) : (
              <List dense>
                {queueData.queue.map((item, index) => (
                  <ListItem key={item.id} divider={index < queueData.queue.length - 1}>
                    <Box sx={{ width: '100%' }}>
                      {/* Item Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {getStatusIcon(item.status)}
                        <Typography variant="body2" sx={{ ml: 1, flex: 1, fontWeight: 500 }}>
                          {item.filename}
                        </Typography>
                        <Chip
                          label={item.status}
                          color={getStatusColor(item.status)}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                        {item.status === 'queued' && (
                          <IconButton
                            size="small"
                            onClick={() => removeFromQueue(item.id)}
                            title="Remover de cola"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Progress Bar */}
                      {item.status === 'processing' && (
                        <LinearProgress
                          variant="determinate"
                          value={item.progress || 0}
                          sx={{ mb: 1, height: 4 }}
                        />
                      )}

                      {/* Item Details */}
                      <Typography variant="caption" color="text.secondary">
                        Tamaño: {formatFileSize(item.file_size)} |
                        Agregado: {new Date(item.added_at).toLocaleTimeString('es-ES')}
                      </Typography>

                      {/* Error Message */}
                      {item.error && (
                        <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                          <Typography variant="caption">{item.error}</Typography>
                        </Alert>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QueuePanel;